/**
 * PageViewTracker
 * 
 * Tracks page views on route changes for SPA navigation.
 * Uses useExternalAnalytics to send events to GA4 + Meta Pixel
 * with consent awareness.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useExternalAnalytics } from '@/hooks/useExternalAnalytics';
import { ANALYTICS_CONFIG } from '@/lib/analyticsConfig';
import { getModuleFromPath, getModuleSurface } from '@/lib/modules';
import { logger } from "@/lib/logger";

export function PageViewTracker() {
  const location = useLocation();
  const { trackPageView, trackBoth, canTrackGA4, canTrackMeta } = useExternalAnalytics();
  const isFirstRender = useRef(true);
  const previousPath = useRef<string | null>(null);
  const lastModuleSlug = useRef<string | null>(null);

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Skip if same path (e.g., hash change only)
    if (previousPath.current === currentPath) {
      return;
    }
    
    // Track page view (including initial load)
    // Small delay to ensure page title is updated
    const timeoutId = setTimeout(() => {
      trackPageView(location.pathname, document.title);

      // Fire `module_view` when the route resolves to one of the 8 modules
      // and the module slug changed since the last navigation. Within-module
      // deep-link navigation does NOT re-fire to keep the event meaningful
      // for "engagement per module" reports.
      const mod = getModuleFromPath(location.pathname);
      if (mod && mod.slug !== lastModuleSlug.current) {
        lastModuleSlug.current = mod.slug;
        trackBoth('module_view', {
          module: mod.slug,
          module_name: mod.name,
          surface: getModuleSurface(location.pathname) ?? 'app',
          path: location.pathname,
        });
      } else if (!mod) {
        // Reset so re-entering a module from outside re-fires module_view.
        lastModuleSlug.current = null;
      }
      
      if (ANALYTICS_CONFIG.debugMode) {
        logger.debug('[PageViewTracker] Page view tracked:', {
          path: location.pathname,
          title: document.title,
          isInitial: isFirstRender.current,
          module: getModuleFromPath(location.pathname)?.slug ?? null,
          canTrackGA4,
          canTrackMeta,
        });
      }
    }, 100);

    previousPath.current = currentPath;
    isFirstRender.current = false;

    return () => clearTimeout(timeoutId);
  }, [location, trackPageView, trackBoth, canTrackGA4, canTrackMeta]);

  return null; // This component renders nothing
}

export default PageViewTracker;
